import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export type UserRole = "admin" | "member" | "viewer";

export interface AuthenticatedUser {
  id: string;
  email: string;
  role: UserRole;
  is_active: boolean;
  openstack_user_id: string | null;
  openstack_project_id: string | null;
  /** Which sections the menu shows (SASE plan SD-01); absent on a session
   *  stored before SASE-1, which reads as "everything". */
  product_view?: "everything" | "sase_only" | "cloud_only";
}

interface AuthState {
  accessToken: string | null;
  expiresAt: number | null;
  user: AuthenticatedUser | null;
  setSession: (params: {
    accessToken: string;
    expiresIn: number;
    user: AuthenticatedUser;
  }) => void;
  /** Swap in a refreshed access token without touching the stored user. */
  setAccessToken: (accessToken: string, expiresIn: number) => void;
  updateUser: (user: AuthenticatedUser) => void;
  clear: () => void;
  isAuthenticated: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      expiresAt: null,
      user: null,
      setSession: ({ accessToken, expiresIn, user }) =>
        set({
          accessToken,
          expiresAt: Date.now() + expiresIn * 1000,
          user,
        }),
      setAccessToken: (accessToken, expiresIn) =>
        set({
          accessToken,
          expiresAt: Date.now() + expiresIn * 1000,
        }),
      updateUser: (user) => set({ user }),
      clear: () => set({ accessToken: null, expiresAt: null, user: null }),
      isAuthenticated: () => {
        const { accessToken, expiresAt } = get();
        if (!accessToken || !expiresAt) return false;
        return Date.now() < expiresAt;
      },
    }),
    {
      name: "cloudpanel.auth",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        accessToken: state.accessToken,
        expiresAt: state.expiresAt,
        user: state.user,
      }),
    },
  ),
);
